/**
 * Format a date string as "DD/MM, HH:mm"
 */
export function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}, ${hours}:${minutes}`;
}

/**
 * Format a date string as "D/MM/YYYY - HH:mm"
 */
export function formatFullDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} - ${hours}:${minutes}`;
}

/**
 * Format a date string as "DD/MM/YYYY"
 */
export function formatDateOnly(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format a date string as "HH:mm"
 */
export function formatTimeOnly(dateString: string): string {
  const date = new Date(dateString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Format a "YYYY-MM-DD" date string as "DD/MM/YYYY" without timezone conversion.
 */
export function formatYmdToDmy(date: string): string {
  const parts = date.split('-');
  if (parts.length !== 3) return date;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

/**
 * Format a Date as a "YYYY-MM-DD" key using the browser local time.
 */
export function toLocalYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export type DurationGranularity = 'minutes-seconds' | 'hours-minutes';

/**
 * Format a duration in seconds with the requested granularity.
 *
 * - `minutes-seconds` -> "Xm Ys" (used by per-activity / per-KC labels).
 * - `hours-minutes`   -> "Xh Ym" / "Xh" / "Xm" / "<1m" / "0m" (used by
 *   aggregate-scale labels where seconds are noise — e.g. per-course practice
 *   time on the teacher's students list).
 *
 * `null` / `undefined` -> "—" in both modes so the absence of data is visually
 * distinct from a genuine zero. Negative or non-finite inputs are coerced to
 * the equivalent of zero — the formatter must not throw on bad inputs.
 */
export function formatDuration(seconds: number | null | undefined, granularity: DurationGranularity): string {
  if (seconds === null || seconds === undefined) return '—';

  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;

  // `minutes-seconds` branches first because the rest of the function (the "0m" /
  // "<1m" shortcuts and the "Xh Ym" composition) is `hours-minutes`-only — at the
  // per-activity scale seconds are signal, so 0s → "0m 0s" and 30s → "0m 30s".
  if (granularity === 'minutes-seconds') {
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  if (safeSeconds === 0) return '0m';
  if (safeSeconds < 60) return '<1m';

  const totalMinutes = Math.floor(safeSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Get initials from first name and last name
 */
export function getInitials(name: string | null, lastName: string | null): string {
  const firstInitial = name?.charAt(0)?.toUpperCase() ?? '';
  const lastInitial = lastName?.charAt(0)?.toUpperCase() ?? '';
  return `${firstInitial}${lastInitial}` || '?';
}
